/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect } from "react";
import { Box, Center, Link, SimpleGrid } from "@chakra-ui/react";
import PrevArrowIcon from "assets/img/layout/previou-arrow.svg";
import PageLayout from "components/layout/PageLayout";
import AppSpinner from "components/spinner/AppSpinner";
import { useNavigate } from "react-router-dom";
import RevisionRequestModal from "./components/RevisionRequestModal";
import ProjectProgressCard from "./components/ProjectProgressCard";
import ProjectOverviewCard from "./components/ProjectOverviewCard";
import ProjectAdditionalServices from "./components/ProjectAdditionalServices";
import { Notification } from "./components/Notification";
import { useProjectDetail } from "views/customer/project-detail/hooks/useProjectDetail";
import { useProjectActions } from "views/customer/project-detail/hooks/useProjectActions";
import { useRevisionModal } from "views/customer/project-detail/hooks/useRevisionModal";
import { useProjectServices } from "views/customer/project-detail/hooks/useProjectServices";
import { useRevisionRequest } from "views/customer/project-detail/hooks/useRevisionRequest";

export default function CustomerProjectDetail() {
  const navigate = useNavigate();

  const {
    selectedProject,
    progressPercentage,
    documents,
    refreshProject,
    trackingDetails,
    loading,
    projectManagerIds,
  } = useProjectDetail();

  const {
    successMessage,
    errorMessage,
    onDownloadFiles,
    setErrorMessage,
    clearMessages,
  } = useProjectActions();

  const {
    isOpen: isRevisionModalOpen,
    revisionText,
    onOpen: onRevisionModalOpen,
    onClose: onRevisionModalClose,
    onChangeRevisionText,
    clearRevisionText,
  } = useRevisionModal();

  const {
    isSubmitting: isSubmittingRevision,
    submitRevisionRequest,
    clearAttachedFiles,
  } = useRevisionRequest(projectManagerIds);

  const handleRevisionSubmit = async () => {
    const result = await submitRevisionRequest(revisionText);

    if (result.success) {
      clearRevisionText();
      clearAttachedFiles();
      onRevisionModalClose();
      navigate("/customer/support?tab=messages");
      return;
    }

    setErrorMessage(result.message);
    clearRevisionText();
    clearAttachedFiles();
    onRevisionModalClose();
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const {
    includedAddons,
    typeAddons,
    crossTypeAvailableServices,
    crossTranslationType,
  } = useProjectServices(selectedProject);

  useEffect(() => {
    clearMessages();
    return () => {
      clearMessages();
    };
  }, [selectedProject?.order?.order_id, clearMessages]);

  return (
    <PageLayout
      breadcrumb={
        <Link
          onClick={() => navigate("/customer/projects")}
          display="flex"
          alignItems="center"
          fontWeight="400"
          _hover={{ textDecoration: "underline" }}
        >
          <Box
            as="img"
            src={PrevArrowIcon}
            fill="black"
            w="20px"
            h="20px"
            mr="8px"
          />
          Back to Projects
        </Link>
      }
    >
      {loading || !selectedProject ? (
        <Center w="100%" minH="60vh">
          <AppSpinner />
        </Center>
      ) : (
        <>
          <Notification
            successMessage={successMessage}
            errorMessage={errorMessage}
          />

          <ProjectProgressCard
            selectedProject={selectedProject}
            progressPercentage={progressPercentage}
          />

          <SimpleGrid
            columns={{ base: 1 }}
            gap="24px"
            templateColumns={{ base: "1fr", lg: "66.226% minmax(0, 1fr)" }}
          >
            <Box>
              <ProjectOverviewCard
                selectedProject={selectedProject}
                includedAddons={includedAddons}
                typeAddons={typeAddons}
                documents={documents}
                hasAttachments={documents.length > 0}
                onDownloadFiles={onDownloadFiles}
                onOpenRevisionModal={() => {
                  clearAttachedFiles();
                  onRevisionModalOpen();
                }}
                onRefreshProject={refreshProject}
                trackingDetails={trackingDetails}
              />
            </Box>

            <Box>
              <ProjectAdditionalServices
                availableServices={crossTypeAvailableServices}
                translationType={crossTranslationType}
              />
            </Box>
          </SimpleGrid>

          <RevisionRequestModal
            isOpen={isRevisionModalOpen}
            onClose={onRevisionModalClose}
            revisionText={revisionText}
            onChangeRevisionText={onChangeRevisionText}
            onSubmit={handleRevisionSubmit}
            isSubmitting={isSubmittingRevision}
          />
        </>
      )}
    </PageLayout>
  );
}
